<template>
  <dashboard-collapsible-card
    :title="computedTitle"
    :show-title="false"
    :collapsed="collapsed"
    :card-class="computedCardClass"
    header-class="flex items-start justify-end gap-3"
    collapsed-icon="fas fa-newspaper text-primary-600"
    content-class="flex-1 flex flex-col gap-4"
    @toggle="$emit('toggle')"
  >
    <template #header-actions-after>
      <button
        v-if="showCreateButton"
        type="button"
        class="absolute top-4 right-4 items-center justify-center rounded-2xl bg-gradient-to-r from-primary-500 to-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed w-36"
        :disabled="!canCreate || isOpening"
        @click="$emit('create')"
      >
        <span v-if="isOpening"><i class="fas fa-spinner fa-spin"></i></span>
        <span v-else>Ajouter une actualité</span>
      </button>
    </template>

    <template #collapsed-description>
      <p class="text-xs text-gray-500">
        <span v-if="loadingPendingNews || loadingMavieproNews">Chargement...</span>
        <span v-else>{{ formatNumber(collapsedItemsCount) }} élément(s)</span>
      </p>
    </template>

    <div class="grid gap-6 md:grid-cols-2 flex-1 items-stretch">
      <section v-if="showInactive" class="flex h-full flex-col gap-3">
        <div class="flex items-center justify-between">
          <h4 class="dashboard-action-section-title">Actualités inactives</h4>
        </div>
        <dashboard-list-state
          :loading="loadingPendingNews"
          :has-data="pendingNews.length"
          class="flex-1 flex flex-col overflow-hidden"
        >
          <template #default>
            <div
              class="flex-1 overflow-y-auto max-h-60 space-y-3 pr-3"
              style="scrollbar-gutter: stable;"
            >
              <div
                v-for="item in displayedPendingNews"
                :key="item.uuid || item.id"
                class="flex items-center justify-between gap-4 rounded-2xl border border-gray-200 bg-white px-4 py-3"
              >
                <div>
                  <div class="flex items-center gap-2">
                    <p class="text-sm text-gray-900">{{ item.title }}</p>
                    <button
                      type="button"
                      class="text-gray-400 hover:text-primary-500 transition focus:outline-none focus:text-primary-500"
                      @click.stop="previewNews(item, 'pending')"
                      aria-label="Aperçu de l'actualité"
                    >
                      <i class="fas fa-eye"></i>
                    </button>
                  </div>
                  <p class="text-xs text-gray-500">
                    <span v-if="item.publication_date">
                      Publication prévue le {{ formatDate(item.publication_date) }}
                    </span>
                    <span v-else>
                      Publication prévue le Date à confirmer
                    </span>
                  </p>
                </div>
                <button
                  type="button"
                  class="dashboard-primary-button"
                  @click="$emit('open', item)"
                >
                  Activer
                </button>
              </div>
            </div>
          </template>
          <template #loading>
            <div class="flex-1 overflow-hidden space-y-3 pr-3" style="scrollbar-gutter: stable;">
              <div
                v-for="index in 3"
                :key="index"
                class="flex items-center justify-between gap-4 rounded-2xl border border-dashed border-gray-200 bg-white px-4 py-3"
              >
                <div class="flex-1 space-y-2">
                  <div class="dashboard-skeleton h-4 w-3/4 rounded-full"></div>
                  <div class="dashboard-skeleton h-3 w-1/2 rounded-full"></div>
                </div>
                <span class="dashboard-skeleton h-8 w-24 rounded-full"></span>
              </div>
            </div>
          </template>
          <template #empty>
            <div class="flex flex-1 items-center justify-center rounded-2xl bg-gray-100 px-4 py-6 text-sm text-gray-600">
              Aucune actualité en attente
            </div>
          </template>
        </dashboard-list-state>
      </section>

      <section v-if="showMaviepro" class="flex h-full flex-col gap-3 md:ml-5">
        <div class="flex items-center justify-between">
          <h4 class="dashboard-action-section-title">{{ mavieproMediaHeading }}</h4>
        </div>
        <dashboard-list-state
          :loading="loadingMavieproNews"
          :has-data="mavieproNews.length"
          class="flex-1 flex flex-col overflow-hidden"
        >
          <template #default>
            <div
              class="flex-1 overflow-y-auto max-h-60 space-y-3 pr-1"
              style="scrollbar-gutter: stable;"
            >
              <div
                v-for="item in displayedMavieproNews"
                :key="item.uuid"
                class="flex items-center justify-between gap-4 rounded-2xl border border-primary-200 bg-white px-4 py-3"
              >
                <div>
                  <div class="flex items-center gap-2">
                    <p class="text-sm text-gray-900">{{ item.title }}</p>
                    <button
                      type="button"
                      class="text-gray-400 hover:text-primary-500 transition focus:outline-none focus:text-primary-500"
                      @click.stop="previewNews(item, 'maviepro')"
                      aria-label="Aperçu de l'actualité"
                    >
                      <i class="fas fa-eye"></i>
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  class="dashboard-primary-button"
                  :disabled="duplicating"
                  @click="$emit('duplicate', item)"
                >
                  <span v-if="duplicating"><i class="fas fa-spinner fa-spin"></i></span>
                  <span v-else>Dupliquer</span>
                </button>
              </div>
            </div>
          </template>
          <template #loading>
            <div class="flex-1 overflow-hidden space-y-3 pr-1" style="scrollbar-gutter: stable;">
              <div
                v-for="index in 3"
                :key="index"
                class="flex items-center justify-between gap-4 rounded-2xl border border-dashed border-primary-200 bg-white px-4 py-3"
              >
                <div class="flex-1 space-y-2">
                  <div class="dashboard-skeleton h-4 w-2/3 rounded-full"></div>
                  <div class="dashboard-skeleton h-3 w-1/2 rounded-full"></div>
                </div>
                <span class="dashboard-skeleton h-8 w-24 rounded-full"></span>
              </div>
            </div>
          </template>
          <template #empty>
            <div class="flex flex-1 items-center justify-center rounded-2xl bg-gray-100 px-4 py-6 text-sm text-gray-600">
              Aucune actualité {{ mavieproLabel }} à afficher
            </div>
          </template>
        </dashboard-list-state>
      </section>
    </div>
  </dashboard-collapsible-card>
</template>

<script>
import { mapGetters } from 'vuex'
import DashboardCollapsibleCard from '@/components/dashboard/DashboardCollapsibleCard.vue'
import DashboardListState from '@/components/dashboard/DashboardListState.vue'
import DashboardNewsPreviewModal from '@/components/dashboard/DashboardNewsPreviewModal.vue'

export default {
  name: 'DashboardPendingNewsCard',
  components: {
    DashboardCollapsibleCard,
    DashboardListState
  },
  props: {
    collapsed: {
      type: Boolean,
      required: true
    },
    loadingPendingNews: {
      type: Boolean,
      default: false
    },
    pendingNews: {
      type: Array,
      default: () => []
    },
    loadingMavieproNews: {
      type: Boolean,
      default: false
    },
    mavieproNews: {
      type: Array,
      default: () => []
    },
    titleOverride: {
      type: String,
      default: ''
    },
    canCreate: {
      type: Boolean,
      default: false
    },
    isOpening: {
      type: Boolean,
      default: false
    },
    duplicating: {
      type: Boolean,
      default: false
    },
    totalPending: {
      type: Number,
      default: 0
    },
    formatNumber: {
      type: Function,
      required: true
    },
    formatDate: {
      type: Function,
      required: true
    },
    showInactive: {
      type: Boolean,
      default: true
    },
    showMaviepro: {
      type: Boolean,
      default: true
    },
    showCreateButton: {
      type: Boolean,
      default: true
    },
    cardClass: {
      type: String,
      default: ''
    }
  },
  emits: ['toggle', 'create', 'open', 'duplicate'],
  computed: {
    ...mapGetters(['application']),
    computedTitle () {
      if (this.titleOverride) return this.titleOverride
      return ''
    },
    computedCardClass () {
      return [
        'h-full flex flex-col',
        this.showCreateButton ? 'gap-4' : '',
        this.cardClass
      ].filter(Boolean).join(' ')
    },
    displayedPendingNews () {
      return (this.pendingNews || []).slice(0, 3)
    },
    displayedMavieproNews () {
      return (this.mavieproNews || []).slice(0, 3)
    },
    collapsedItemsCount () {
      const pendingCount = this.showInactive ? this.displayedPendingNews.length : 0
      const mavieproCount = this.showMaviepro ? this.displayedMavieproNews.length : 0
      return pendingCount + mavieproCount
    },
    mavieproLabel () {
      const label = this.application?.app?.label
      if (!label) {
        return ''
      }
      return label.charAt(0).toUpperCase() + label.slice(1)
    },
    mavieproMediaHeading () {
      const label = this.mavieproLabel || 'Maviepro'
      return `Média ${label}`
    }
  },
  methods: {
    previewNews (item, source) {
      const origin = typeof source === 'string' && source ? source : 'pending'
      const uuid = item?.uuid || item?.id
      if (!uuid) {
        return
      }

      const modal = this.$buefy.modal.open({
        parent: this,
        component: DashboardNewsPreviewModal,
        hasModalCard: false,
        trapFocus: true,
        customClass: 'dashboard-modal flex items-center justify-center py-6 lg:py-10',
        contentClass: 'w-full max-w-4xl mx-4 flex',
        canCancel: ['escape', 'outside'],
        props: {
          articleUuid: uuid,
          initialTitle: item?.title || '',
          source: origin
        },
        events: {
          close: () => {
            modal.close()
          }
        }
      })
      return modal
    }
  }
}
</script>
